import { useMemo } from "react";
import styled from "@emotion/styled";
import { Box, keyframes, Stack, Tooltip } from "@mui/material";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { ExchangeStatus, CalculatorStats } from "src/Modules/Arbitrage/types";
import { DataGrid, GridColumns, GridToolbar } from "@mui/x-data-grid";
import { DateTime, Interval } from "luxon";
import humanizeDuration from "humanize-duration";

interface Props {
  data: ExchangeStatus[];
  isLoading: boolean;
  calculatorCoverage?: CalculatorStats["exchangeCoverage"];
}

export const ExchangesStatusTable = ({
  data,
  isLoading,
  calculatorCoverage,
}: Props) => {
  // Merge calculator coverage into data
  const enrichedData = useMemo(() => {
    return data.map((exchange) => ({
      ...exchange,
      calcPairs: calculatorCoverage?.[exchange.id]?.pairs ?? 0,
      calcSymbols: calculatorCoverage?.[exchange.id]?.symbols ?? 0,
    }));
  }, [data, calculatorCoverage]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        headerName: "Exchange",
        field: "id",
        type: "string",
        minWidth: 150,
        renderCell: ({ value }) => (
          <Box display="flex" alignItems="center">
            <ExchangeIcon name={value} style={{ height: "1.2rem" }} />
            <Label>{value}</Label>
          </Box>
        ),
      },
      {
        headerName: "Status",
        field: "available",
        type: "string",
        width: 120,
        renderCell: ({ value }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            {value ? <DotIcon /> : <DotIconEmpty />}
            <Label>{value ? "Available" : "Unavailable"}</Label>
          </Stack>
        ),
      },
      {
        headerName: "Pairs (Agg)",
        field: "activePairs",
        type: "number",
        width: 100,
        align: "center",
        headerAlign: "center",
        description: "Pairs in Aggregator (from connectors)",
        renderCell: ({ value }) => <MetricValue>{value ?? "-"}</MetricValue>,
      },
      {
        headerName: "Pairs (Calc)",
        field: "calcPairs",
        type: "number",
        width: 100,
        align: "center",
        headerAlign: "center",
        description: "Pairs in Calculator (processing)",
        renderCell: ({ value }) => (
          <MetricValueAlt>{value ?? "-"}</MetricValueAlt>
        ),
      },
      {
        headerName: "Symbols (Agg)",
        field: "activeSymbols",
        type: "number",
        width: 110,
        align: "center",
        headerAlign: "center",
        description: "Symbols in Aggregator",
        renderCell: ({ value }) => <MetricValue>{value ?? "-"}</MetricValue>,
      },
      {
        headerName: "Symbols (Calc)",
        field: "calcSymbols",
        type: "number",
        width: 110,
        align: "center",
        headerAlign: "center",
        description: "Symbols in Calculator",
        renderCell: ({ value }) => (
          <MetricValueAlt>{value ?? "-"}</MetricValueAlt>
        ),
      },
      {
        headerName: "Last update",
        field: "lastUpdate",
        type: "string",
        flex: 1,
        minWidth: 150,
        renderCell: ({ value }) => {
          if (!value) return "-";
          const dt = DateTime.fromJSDate(new Date(value));
          const duration = Interval.fromDateTimes(DateTime.now(), dt)
            .toDuration()
            .valueOf();
          return (
            <Tooltip
              arrow
              title={`( ${humanizeDuration(duration, {
                language: "en",
                units: ["y", "mo", "d", "h", "m"],
                maxDecimalPoints: 1,
              })} )`}
            >
              <div>
                <div>
                  {dt.toLocaleString({
                    weekday: "short",
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </div>
                <div>{dt.toLocaleString(DateTime.TIME_SIMPLE)}</div>
              </div>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  return (
    <Box height="60vh">
      <DataGrid
        loading={isLoading}
        style={{ border: "none" }}
        headerHeight={36}
        density="standard"
        rows={enrichedData}
        columns={columns}
        rowsPerPageOptions={[50, 100]}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
    </Box>
  );
};

const Label = styled.span({
  fontWeight: 700,
  paddingLeft: "0.5rem",
  fontSize: "0.7rem",
});

const MetricValue = styled.span(({ theme }: any) => ({
  fontWeight: 600,
  fontSize: "0.85rem",
  color: theme?.palette?.primary?.main ?? "#1976d2",
}));

const MetricValueAlt = styled.span(({ theme }: any) => ({
  fontWeight: 600,
  fontSize: "0.85rem",
  color: theme?.palette?.success?.main ?? "#2e7d32",
}));

const bounce = keyframes`
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(62, 228, 55, 0.7);
    }
    
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 5px rgba(62, 228, 55, 0);
    }
    
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(62, 228, 55, 0);
    }
`;

const DotIcon = styled.div(() => ({
  background: "rgba(62, 228, 55)",
  borderRadius: "50%",
  height: ".6rem",
  width: ".6rem",
  boxShadow: "0 0 0 0 rgba(62, 228, 55)",
  transform: "scale(1)",
  animation: `${bounce} 2s ease infinite`,
}));

const DotIconEmpty = styled.div((props: any) => ({
  borderRadius: "50%",
  height: ".6rem",
  width: ".6rem",
  backgroundColor: props.theme.palette.error.main,
  //   border: "1px solid white",
}));
